/**
 * EmptyPreview — the skeleton-mock archetypes for the Product-Preview empty
 * state (see `<PageState state="empty" preview="…" />`). Each renders a dimmed,
 * theme-aware mock of a populated screen's REAL layout (built from the Skeleton
 * primitive, no screenshot assets) so a first-run empty previews the shape of
 * the content that will land.
 *
 * One archetype per content family. Two pages that share a shape still read
 * differently because the headline / body / CTA / demo chip below differ.
 * Pure presentation — no data, no props beyond `shape`.
 */
import { type ReactElement } from 'react';
import { Skeleton } from '../Skeleton';

export type PreviewShape =
  | 'card-grid' // registry of cards (agents, dashboard)
  | 'table' // dense log/ledger table (audit, api-logs, activity-audit)
  | 'queue' // decision queue rows (plans, intent-plans)
  | 'list' // simple member/key/session list rows
  | 'timeline' // event feed / session traces
  | 'graph' // node-edge topology
  | 'stat-tiles' // KPI tile row + chart (dashboard, api-usage, engine)
  | 'form'; // connect/config panel (settings engine)

function CardGridMock(): ReactElement {
  return (
    <div className="grid grid-cols-3 gap-3">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="border-aq-border bg-aq-surface rounded-lg border p-3">
          <div className="flex items-center gap-2">
            <Skeleton variant="circle" width={22} height={22} />
            <Skeleton width="60%" />
          </div>
          <div className="mt-3 space-y-1.5">
            <Skeleton width="100%" />
            <Skeleton width="75%" />
          </div>
          <div className="mt-3 flex gap-1.5">
            <Skeleton variant="rect" width={34} height={14} />
            <Skeleton variant="rect" width={34} height={14} />
          </div>
        </div>
      ))}
    </div>
  );
}

function TableMock(): ReactElement {
  return (
    <div className="border-aq-border bg-aq-surface overflow-hidden rounded-lg border">
      <div className="border-aq-border bg-aq-zebra flex items-center gap-4 border-b px-4 py-2.5">
        <Skeleton width={90} />
        <Skeleton width={70} />
        <div className="flex-1" />
        <Skeleton width={54} />
        <Skeleton width={40} />
      </div>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className="border-aq-border flex items-center gap-4 border-b px-4 py-2.5 last:border-0"
        >
          <Skeleton variant="circle" width={18} height={18} />
          <Skeleton width={`${34 - i * 2}%`} />
          <div className="flex-1" />
          <Skeleton width={54} />
          <Skeleton variant="rect" width={40} height={16} />
        </div>
      ))}
    </div>
  );
}

function QueueMock(): ReactElement {
  return (
    <div className="space-y-2.5">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="border-aq-border bg-aq-surface flex items-center gap-3 rounded-lg border px-4 py-3"
        >
          <Skeleton variant="rect" width={4} height={36} />
          <Skeleton variant="circle" width={26} height={26} />
          <div className="flex-1 space-y-1.5">
            <Skeleton width={`${52 - i * 8}%`} />
            <Skeleton width="34%" />
          </div>
          <Skeleton variant="rect" width={52} height={22} />
          <Skeleton variant="rect" width={52} height={22} />
        </div>
      ))}
    </div>
  );
}

function ListMock(): ReactElement {
  return (
    <div className="border-aq-border bg-aq-surface divide-aq-border divide-y overflow-hidden rounded-lg border">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3">
          <Skeleton variant="circle" width={24} height={24} />
          <div className="flex-1 space-y-1.5">
            <Skeleton width={`${48 - i * 5}%`} />
            <Skeleton width="30%" />
          </div>
          <Skeleton variant="rect" width={56} height={18} />
        </div>
      ))}
    </div>
  );
}

function TimelineMock(): ReactElement {
  return (
    <div className="relative pl-5">
      <span className="bg-aq-border absolute top-1 bottom-1 left-[7px] w-px" />
      <div className="space-y-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="relative">
            <span className="border-aq-bg bg-aq-border absolute top-0.5 -left-5 h-3.5 w-3.5 rounded-full border-2" />
            <div className="space-y-1.5">
              <Skeleton width={`${56 - i * 6}%`} />
              <Skeleton width="40%" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GraphMock(): ReactElement {
  // A tiny node-edge cluster. Positions are fixed so it reads as a topology.
  const nodes = [
    { x: 50, y: 12 },
    { x: 20, y: 48 },
    { x: 80, y: 46 },
    { x: 38, y: 84 },
    { x: 70, y: 82 },
  ];
  const edges = [
    [0, 1],
    [0, 2],
    [1, 3],
    [2, 4],
    [1, 2],
  ];
  return (
    <div className="relative mx-auto h-40 w-full max-w-sm">
      <svg viewBox="0 0 100 100" className="h-full w-full" preserveAspectRatio="xMidYMid meet">
        {edges.map(([a, b], i) => (
          <line
            key={i}
            x1={nodes[a]!.x}
            y1={nodes[a]!.y}
            x2={nodes[b]!.x}
            y2={nodes[b]!.y}
            className="stroke-aq-border"
            strokeWidth={0.8}
          />
        ))}
        {nodes.map((n, i) => (
          <circle
            key={i}
            cx={n.x}
            cy={n.y}
            r={i === 0 ? 7 : 5}
            className={
              i === 0 ? 'fill-aq-accent-soft stroke-aq-accent' : 'fill-aq-surface stroke-aq-border'
            }
            strokeWidth={0.8}
          />
        ))}
      </svg>
    </div>
  );
}

function StatTilesMock(): ReactElement {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="border-aq-border bg-aq-surface rounded-lg border p-3">
            <Skeleton width="55%" />
            <Skeleton width="70%" height={18} className="mt-2" />
          </div>
        ))}
      </div>
      <div className="border-aq-border bg-aq-surface flex h-24 items-end gap-1.5 rounded-lg border p-3">
        {[40, 62, 48, 74, 55, 82, 60, 90, 70, 58, 78, 66].map((h, i) => (
          <Skeleton key={i} variant="rect" width="100%" height={`${h}%`} className="flex-1" />
        ))}
      </div>
    </div>
  );
}

function FormMock(): ReactElement {
  return (
    <div className="border-aq-border bg-aq-surface space-y-4 rounded-lg border p-4">
      {[0, 1, 2].map((i) => (
        <div key={i} className="space-y-1.5">
          <Skeleton width={`${28 + i * 6}%`} />
          <Skeleton variant="rect" width="100%" height={34} />
        </div>
      ))}
      <div className="flex justify-end gap-2 pt-1">
        <Skeleton variant="rect" width={72} height={30} />
        <Skeleton variant="rect" width={92} height={30} />
      </div>
    </div>
  );
}

const SHAPES: Record<PreviewShape, () => ReactElement> = {
  'card-grid': CardGridMock,
  table: TableMock,
  queue: QueueMock,
  list: ListMock,
  timeline: TimelineMock,
  graph: GraphMock,
  'stat-tiles': StatTilesMock,
  form: FormMock,
};

export function EmptyPreview({ shape }: { shape: PreviewShape }): ReactElement {
  const Mock = SHAPES[shape];
  return <Mock />;
}
